import { Event } from '@api/models/event/event';
import { EventsHttpService } from '@api/http/events/events-http.service';
import {IEventTeam, ILineupPlayer} from '@api/models/event/event-team';
import {EventEmitter, Injectable} from '@angular/core';
import { League } from '@api/models/league/league';
import { LeagueHttpService } from '@api/http/league/league-http.service';
import {EventStatusStringMap} from '@api/models/event/event-status';
import {UrlService} from '@utils/services/url/url.service';
import {DEFAULT_ID_LENGTH} from "@api/http/http.service";
import {IEventStatusResponse} from "@api/models/event/event-status-response";
import {IEventTabs} from "@api/models/event/event-tabs";
import { MatchRoutePaths } from '@lib-modules/match/match-route-paths';
import { ISponsor } from '@api/models/sponsor/sponsor';
import {Team} from '@api/models/team/team';
import {ActivatedRoute, Router} from '@angular/router';
import { IUser } from '../../../../../api/src/lib/models/user/user';
import { BehaviorSubject, Subscription } from 'rxjs';

interface IMatchTab {
  path: string;
  name: string;
  active: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MatchService {
  public loading = true;
  public match: Event;
  public league: League;
  public teams: { home: IEventTeam; away: IEventTeam };
  public scores: { home: string, away: string } = { home: '-', away: '-' };
  public currentMinute = '';
  public selectedTeamId: string;
  public isHomeTeam: boolean;
  public tabs: IMatchTab[] = [
    { path: MatchRoutePaths.Infos, name: 'Infos', active: true },
    { path: MatchRoutePaths.Ticker, name: 'Liveticker', active: false },
    { path: MatchRoutePaths.Videos, name: 'Videos', active: false },
    { path: MatchRoutePaths.Overview, name: 'Übersicht', active: false },
    { path: MatchRoutePaths.Lineup, name: 'Aufstellung', active: false },
    { path: MatchRoutePaths.Table, name: 'Tabelle', active: true },
  ];
  public sponsors: ISponsor[];
  public tickerUrl: string;
  public matchDate: Date;
  public user: IUser;
  public ready = new EventEmitter();

  constructor(
    private eventsHttpService: EventsHttpService,
    private leagueHttpService: LeagueHttpService,
  ) {}

  public async init(eventId: string, teamId?: string): Promise<void> {
    this.loading = true;
    try {
      if (!eventId) {
        console.error('fan.at Match Widget: Event Id is not defined!');
        return;
      }
      if (eventId.length !== DEFAULT_ID_LENGTH) {
        const response = await this.eventsHttpService.getEventByLoalaId(eventId);
        eventId = response.data?._id;
      }
      const matchData = await this.eventsHttpService.getEvent(eventId, false);
      if (!matchData) {
        console.error('fan.at Match Widget: Match data is empty!');
        return;
      }
      this.match = new Event(matchData);
      this.matchDate = new Date(this.match.timestamps.start);
      const teams = this.match.teams;
      this.setScores(teams.home.score, teams.away.score);
      // If given teamId is wrong in any case use the home team id
      this.selectedTeamId = teamId || teams.home.teamid;
      if (
        teamId &&
        teams.home.teamid !== teamId &&
        teams.away.teamid !== teamId
      ) {
        this.selectedTeamId = teams.home.teamid;
      }
      this.isHomeTeam = this.selectedTeamId === teams.home.teamid;
      this.tickerUrl = UrlService.getMatchUrl(this.match._id, this.selectedTeamId);
      this.league = await this.leagueHttpService.getLeague(this.match.leagueid);
      await this.loadTeams();
      this.setSponsors(this.selectedTeamId);
      await this.loadTabs();
      this.loading = false;
    } catch (e) {
      this.loading = false;
      throw e;
    }
  }
  
  public async updateStatus(data?: IEventStatusResponse): Promise<void> {
    if (!data) {
      const response = await this.eventsHttpService.getStatus(this.match._id);
      data = response.data;
    }
    this.currentMinute = data.ui_minute;
    this.setScores(data.teams.home?.score, data.teams.away?.score);
    if (this.match.status !== data.status) {
      await this.reloadEvent();
    }
  }

  public async reloadEvent(): Promise<void> {
    try {
      console.log('%c [TICKER] RELOAD EVENT!', 'color: #1B95E0; font-size: 16px');
      this.match = new Event(await this.eventsHttpService.getEvent(this.match._id));
      await this.loadTabs();

    } catch (e) {
      throw e;
    }
    const teams = this.match.teams;
    this.setScores(teams.home.score, teams.away.score);
  }

  public async reload() {
    await this.init(this.match._id, this.selectedTeamId);
  }

  public getStatusString(): string {
    return EventStatusStringMap.get(this.match.status);
  }

  public isTabWithPathActive(path: string): boolean {
    return this.tabs.find(tab => tab.path === path)?.active || false;
  }

  public getRegularScore(): string | null {
    return this.match.scores?.regular?.secondhalf?.end;
  }

  public showRegularScore(): boolean {
    return this.match.hasPenalty() || this.match.hasOvertime();
  }

  public async loadTabs(): Promise<void> {
    const response = await this.eventsHttpService.getEventTabs(
      this.match._id, !!this.match.sportradar?.sr_match_id
    );
    if (!response.data) {
      return;
    }
    const tabs = response.data;
    this.updateTabs(tabs);
  }

  public updateTabs(tabs: IEventTabs): void {
    const side = this.isHomeTeam ? 'home' : 'away';
    console.log(tabs);
    this.activateTab(MatchRoutePaths.Ticker, tabs.liveticker[side]);
    this.activateTab(MatchRoutePaths.Videos, tabs.videos[side]);
    this.activateTab(MatchRoutePaths.Lineup, tabs.lineup[side]);
    this.activateTab(MatchRoutePaths.Table, tabs.table[side]);
    this.activateTab(MatchRoutePaths.Timeline, tabs.overview[side]);
  }

  public activateTab(path: string, active: boolean) {
    const tab = this.tabs.find(t => t.path === path);
    if (!tab) {
      return;
    }
    tab.active = active;
  }

  public setSponsors(teamId: string): void {
    const sponsors = teamId === this.teams.home.teamid ? this.teams.home.sponsors : this.teams.away.sponsors;
    this.sponsors = sponsors ?? [];
  }

  private async loadTeams(): Promise<void> {
    this.teams = {
      home: this.match.teams.home,
      away: this.match.teams.away,
    };
    this.teams.home._id = this.match.teams.home.teamid;
    this.teams.away._id = this.match.teams.away.teamid;
  }

  public get selectedTeam(): IEventTeam | null {
    if (!this.teams) {
      return null;
    }
    return this.selectedTeamId === this.teams.home.teamid
      ? this.teams.home
      : this.teams.away;
  }

  public setScores(homeScore: number, awayScore: number): void {
    this.scores.home =
      homeScore !== null && homeScore !== -1 ? homeScore.toString() : '-';
    this.scores.away =
      awayScore !== null && awayScore !== -1 ? awayScore.toString() : '-';
  }

  public getLineupPlayerById(playerId: string): ILineupPlayer {
    if (!playerId) {
      return null;
    }
    const players: ILineupPlayer[] = [];
    if (this.match.teams.home) {
      players.push(...Team.getLineupPlayers(this.match.teams.home) || []);
    }
    if (this.match.teams.away) {
      players.push(...Team.getLineupPlayers(this.match.teams.away) || []);
    }
    return players.find(entry => entry.playerid === playerId);
  }
}
